export const MULTI_COMPANY_CONTRACT_VERSION = 'multi-company.v1' as const;

export const IDENTITY_REALMS = {
  MOBILE: 'mobile',
  ADMIN: 'admin'
} as const;

export const TOKEN_AUDIENCES = {
  MOBILE: 'avworkforce-mobile',
  ADMIN: 'avworkforce-admin'
} as const;

export const MULTI_COMPANY_FEATURE_FLAG_NAMES = [
  'multi_company_membership_dual_write',
  'multi_company_membership_read',
  'surface_identity_realms',
  'admin_surface_identity_realms',
  'mobile_contractor_lead_roles',
  'company_explicit_tenant_routes',
  'worker_aggregate_shifts',
  'worker_assignment_conflict_enforcement',
  'admin_explicit_tenant_context',
  'mobile_membership_experience',
  'company_access_code_rotation',
  'legacy_company_id_write_disabled'
] as const;

export type MultiCompanyFeatureFlagName =
  typeof MULTI_COMPANY_FEATURE_FLAG_NAMES[number];
export type MultiCompanyFeatureFlags = Record<
  MultiCompanyFeatureFlagName,
  boolean
>;

export const DISABLED_MULTI_COMPANY_FEATURE_FLAGS: MultiCompanyFeatureFlags =
  Object.freeze(
    Object.fromEntries(
      MULTI_COMPANY_FEATURE_FLAG_NAMES.map((name) => [name, false])
    ) as MultiCompanyFeatureFlags
  );

export const hasAdminSurfaceIdentity = (
  capabilities: MultiCompanyFeatureFlags
): boolean =>
  capabilities.surface_identity_realms ||
  capabilities.admin_surface_identity_realms;

export const MEMBERSHIP_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'suspended',
  'ended',
  'cancelled'
] as const;

export const PLATFORM_STATUSES = ['active', 'suspended', 'blocked'] as const;
export const MOBILE_MEMBERSHIP_ROLES = ['CONTRACTOR', 'LEAD'] as const;
export const ADMIN_ROLES = ['COMPANY_ADMIN', 'SUPER_ADMIN'] as const;

export const BID_STATUSES = [
  'draft',
  'submitted',
  'viewed',
  'accepted',
  'declined',
  'cancelled',
  'expired',
  'conflicted'
] as const;

export const ASSIGNMENT_STATUSES = [
  'pending',
  'assigned',
  'cancelled',
  'completed'
] as const;

export const MULTI_COMPANY_ERROR_CODES = [
  'MULTI_COMPANY_INVALID_REQUEST',
  'MULTI_COMPANY_UNAUTHENTICATED',
  'MULTI_COMPANY_ACCESS_DENIED',
  'MULTI_COMPANY_CONFLICT',
  'MULTI_COMPANY_FEATURE_DISABLED'
] as const;

export const ISOLATION_REASON_CODES = [
  'IDENTITY_REALM_MISMATCH',
  'TOKEN_AUDIENCE_MISMATCH',
  'TENANT_CONTEXT_REQUIRED',
  'TENANT_ACCESS_DENIED',
  'TENANT_RESOURCE_MISMATCH',
  'MEMBERSHIP_REQUIRED',
  'MEMBERSHIP_INACTIVE',
  'COMPANY_ADMIN_GRANT_REQUIRED',
  'COMPANY_ADMIN_SUSPENDED',
  'PLATFORM_ACCOUNT_SUSPENDED',
  'ASSIGNMENT_CONFLICT',
  'FEATURE_DISABLED'
] as const;

export type IdentityRealm =
  typeof IDENTITY_REALMS[keyof typeof IDENTITY_REALMS];
export type TokenAudience =
  typeof TOKEN_AUDIENCES[keyof typeof TOKEN_AUDIENCES];
export type AdminRole = typeof ADMIN_ROLES[number];
export type MultiCompanyErrorCode = typeof MULTI_COMPANY_ERROR_CODES[number];
export type IsolationReasonCode = typeof ISOLATION_REASON_CODES[number];

export type ParsedContractString<T extends string> =
  | { known: true; value: T; raw: string; actionable: true }
  | { known: false; value: null; raw: string; actionable: false };

export interface ParsedMultiCompanySuccessEnvelope<T = unknown> {
  data: T;
  contractVersion: typeof MULTI_COMPANY_CONTRACT_VERSION | null;
  capabilities: MultiCompanyFeatureFlags;
}

export interface ParsedMultiCompanyErrorEnvelope {
  statusCode: number;
  message: string;
  error: string | null;
  code: ParsedContractString<MultiCompanyErrorCode>;
  reasonCode: ParsedContractString<IsolationReasonCode> | null;
  contractVersion: typeof MULTI_COMPANY_CONTRACT_VERSION | null;
  correlationId: string | null;
}

export interface AdminSurfacePrincipal {
  id: number;
  realm: 'admin';
  audience: 'avworkforce-admin';
  role: AdminRole;
}

export interface AdminSurfaceSession {
  principal: AdminSurfacePrincipal;
  accessToken: string;
  refreshToken: string;
}

export interface AdminWorkspaceContextData {
  actorId: number;
  role: 'COMPANY_ADMIN';
  companyAdminGrantId: number;
  accessLevel?: 'COMPANY_ADMIN' | 'OPERATIONS_MANAGER' | 'FINANCE_MANAGER';
  accessLevels?: Array<
    'COMPANY_ADMIN' | 'OPERATIONS_MANAGER' | 'FINANCE_MANAGER'
  >;
  company: {
    id: number;
    name: string;
    /** Absent only on pre-eligibility workspace responses; consumers fail closed. */
    isPrivate?: boolean;
    logo: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
  };
  routeBase: string;
}

export interface AdminWorkspaceSessionData extends AdminWorkspaceContextData {
  /** Present on canonical responses; absence remains fail-closed in clients. */
  capabilities?: MultiCompanyFeatureFlags;
}

export interface CompanyAccessCodeUpdateData {
  companyId: number;
  code: string;
  version: number;
  updatedAt: string;
}

export interface CompanyAccessCodeStateData {
  companyId: number;
  code: string | null;
  version: number | null;
  updatedAt: string | null;
}

export interface PlatformCompanyInventoryItem {
  company: {
    id: number;
    name: string;
    logo: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    lifecycleStatus: string;
    mobileUserCount: number;
    clientCount: number;
    createdAt: string;
  };
  tenantEntryPath: string;
}

export interface PlatformCompanyInventoryData {
  actorId: number;
  role: 'SUPER_ADMIN';
  items: PlatformCompanyInventoryItem[];
  totalCount: number;
  nextAfterCompanyId: number | null;
}

export interface PlatformCompanyChangeHistoryItem {
  id: number;
  actor: {
    id: number;
    role: 'SUPER_ADMIN';
  };
  action: string;
  resourceType: string;
  resourceId: string | null;
  summary: string;
  createdAt: string;
}

export interface PlatformCompanyChangeHistoryData {
  actorId: number;
  role: 'SUPER_ADMIN';
  companyId: number;
  items: PlatformCompanyChangeHistoryItem[];
  nextBeforeAuditId: number | null;
}

export type CompanyApplicationStatus = 'pending' | 'rejected';
export type CompanyMembershipDecision = 'approve' | 'reject' | 'cancel';

export interface CompanyApplication {
  id: number;
  companyId: number;
  internalStatus: CompanyApplicationStatus;
  appliedAt: string | null;
  worker: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string;
  };
}

export type CompanyPendingApplication = CompanyApplication;

export interface CompanyMembershipDecisionData {
  membershipId: number;
  companyId: number;
  workerAccountId: number;
  fromStatus: CompanyApplicationStatus;
  toStatus: 'approved' | 'rejected' | 'cancelled';
  workerStatus: 'pending' | 'approved' | 'cancelled';
  canReapply: boolean;
  notificationDispatch: 'not_required' | 'queued' | 'attention_required';
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const positiveInteger = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0
    ? value
    : null;

const nonNegativeInteger = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;

const optionalString = (value: unknown): string | null | undefined => {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : undefined;
};

const parseOptionalVersion = (
  value: unknown
): typeof MULTI_COMPANY_CONTRACT_VERSION | null | undefined => {
  if (value === undefined || value === null) return null;
  if (value === MULTI_COMPANY_CONTRACT_VERSION) {
    return MULTI_COMPANY_CONTRACT_VERSION;
  }
  return undefined;
};

export const parseContractString = <T extends string>(
  value: unknown,
  knownValues: readonly T[]
): ParsedContractString<T> => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if ((knownValues as readonly string[]).includes(raw)) {
    return {
      known: true,
      value: raw as T,
      raw,
      actionable: true
    };
  }
  return { known: false, value: null, raw, actionable: false };
};

export const parseIdentityRealm = (value: unknown): IdentityRealm | null => {
  const parsed = parseContractString(value, Object.values(IDENTITY_REALMS));
  return parsed.known ? parsed.value : null;
};

export const parseTokenAudience = (value: unknown): TokenAudience | null => {
  const parsed = parseContractString(value, Object.values(TOKEN_AUDIENCES));
  return parsed.known ? parsed.value : null;
};

export const parseAdminRole = (value: unknown): AdminRole | null => {
  const parsed = parseContractString(value, ADMIN_ROLES);
  return parsed.known ? parsed.value : null;
};

export const parseMultiCompanyCapabilities = (
  value: unknown
): MultiCompanyFeatureFlags => {
  if (!isRecord(value)) {
    return { ...DISABLED_MULTI_COMPANY_FEATURE_FLAGS };
  }
  return Object.fromEntries(
    MULTI_COMPANY_FEATURE_FLAG_NAMES.map((name) => [name, value[name] === true])
  ) as MultiCompanyFeatureFlags;
};

export const parseMultiCompanySuccessEnvelope = <T = unknown>(
  value: unknown
): ParsedMultiCompanySuccessEnvelope<T> | null => {
  if (
    !isRecord(value) ||
    !Object.prototype.hasOwnProperty.call(value, 'data')
  ) {
    return null;
  }

  const meta = isRecord(value.meta) ? value.meta : null;
  const version = parseOptionalVersion(meta?.contractVersion);
  if (version === undefined) return null;

  return {
    data: value.data as T,
    contractVersion: version,
    capabilities: parseMultiCompanyCapabilities(meta?.capabilities)
  };
};

export const parseMultiCompanyErrorEnvelope = (
  value: unknown
): ParsedMultiCompanyErrorEnvelope | null => {
  if (
    !isRecord(value) ||
    typeof value.statusCode !== 'number' ||
    typeof value.message !== 'string'
  ) {
    return null;
  }

  const meta = isRecord(value.meta) ? value.meta : null;
  const version = parseOptionalVersion(meta?.contractVersion);
  if (version === undefined) return null;

  return {
    statusCode: value.statusCode,
    message: value.message,
    error: typeof value.error === 'string' ? value.error : null,
    code: parseContractString(value.code, MULTI_COMPANY_ERROR_CODES),
    reasonCode:
      value.reasonCode === undefined
        ? null
        : parseContractString(value.reasonCode, ISOLATION_REASON_CODES),
    contractVersion: version,
    correlationId:
      typeof meta?.correlationId === 'string' ? meta.correlationId : null
  };
};

export const parseAdminSurfacePrincipal = (
  value: unknown
): AdminSurfacePrincipal | null => {
  if (!isRecord(value)) return null;

  const id = positiveInteger(value.id);
  const role = parseAdminRole(value.role);
  if (
    id === null ||
    value.realm !== IDENTITY_REALMS.ADMIN ||
    value.audience !== TOKEN_AUDIENCES.ADMIN ||
    role === null
  ) {
    return null;
  }

  return {
    id,
    realm: IDENTITY_REALMS.ADMIN,
    audience: TOKEN_AUDIENCES.ADMIN,
    role
  };
};

export const parseAdminSurfaceSession = (
  value: unknown
): AdminSurfaceSession | null => {
  if (!isRecord(value)) return null;

  const principal = parseAdminSurfacePrincipal(value.principal);
  if (
    principal === null ||
    typeof value.accessToken !== 'string' ||
    value.accessToken.trim().length === 0 ||
    typeof value.refreshToken !== 'string' ||
    value.refreshToken.trim().length === 0
  ) {
    return null;
  }

  return {
    principal,
    accessToken: value.accessToken,
    refreshToken: value.refreshToken
  };
};

export const parseAdminWorkspaceContext = (
  value: unknown
): AdminWorkspaceContextData | null => {
  if (!isRecord(value) || !isRecord(value.company)) return null;

  const actorId = positiveInteger(value.actorId);
  const companyAdminGrantId = positiveInteger(value.companyAdminGrantId);
  const companyId = positiveInteger(value.company.id);
  const accessLevel = value.accessLevel;
  const accessLevels = Array.isArray(value.accessLevels)
    ? value.accessLevels.filter(
        (
          level
        ): level is NonNullable<
          AdminWorkspaceContextData['accessLevels']
        >[number] =>
          level === 'COMPANY_ADMIN' ||
          level === 'OPERATIONS_MANAGER' ||
          level === 'FINANCE_MANAGER'
      )
    : undefined;
  const isPrivate = value.company.isPrivate;
  const logo = optionalString(value.company.logo);
  const primaryColor = optionalString(value.company.primaryColor);
  const secondaryColor = optionalString(value.company.secondaryColor);
  if (
    actorId === null ||
    companyAdminGrantId === null ||
    companyId === null ||
    value.role !== 'COMPANY_ADMIN' ||
    (accessLevel !== undefined &&
      accessLevel !== 'COMPANY_ADMIN' &&
      accessLevel !== 'OPERATIONS_MANAGER' &&
      accessLevel !== 'FINANCE_MANAGER') ||
    (Array.isArray(value.accessLevels) &&
      accessLevels?.length !== value.accessLevels.length) ||
    typeof value.company.name !== 'string' ||
    value.company.name.trim().length === 0 ||
    (isPrivate !== undefined && typeof isPrivate !== 'boolean') ||
    logo === undefined ||
    primaryColor === undefined ||
    secondaryColor === undefined ||
    value.routeBase !== `/companies/${companyId}`
  ) {
    return null;
  }

  return {
    actorId,
    role: 'COMPANY_ADMIN',
    companyAdminGrantId,
    ...(typeof accessLevel === 'string' ? { accessLevel } : {}),
    ...(accessLevels?.length ? { accessLevels } : {}),
    company: {
      id: companyId,
      name: value.company.name,
      ...(typeof isPrivate === 'boolean' ? { isPrivate } : {}),
      logo,
      primaryColor,
      secondaryColor
    },
    routeBase: value.routeBase
  };
};

export const parseCompanyAccessCodeUpdate = (
  value: unknown,
  expectedCompanyId: number
): CompanyAccessCodeUpdateData | null => {
  if (!isRecord(value)) return null;

  const companyId = positiveInteger(value.companyId);
  const version = positiveInteger(value.version);
  if (
    companyId === null ||
    companyId !== expectedCompanyId ||
    version === null ||
    typeof value.code !== 'string' ||
    value.code.length === 0 ||
    typeof value.updatedAt !== 'string' ||
    Number.isNaN(Date.parse(value.updatedAt))
  ) {
    return null;
  }

  return {
    companyId,
    code: value.code,
    version,
    updatedAt: value.updatedAt
  };
};

export const parseCompanyAccessCodeState = (
  value: unknown,
  expectedCompanyId: number
): CompanyAccessCodeStateData | null => {
  if (!isRecord(value)) return null;

  const companyId = positiveInteger(value.companyId);
  const code = value.code;
  const version = value.version;
  const updatedAt = value.updatedAt;
  if (
    companyId === null ||
    companyId !== expectedCompanyId ||
    (code !== null && (typeof code !== 'string' || code.length === 0)) ||
    (version !== null && positiveInteger(version) === null) ||
    (updatedAt !== null &&
      (typeof updatedAt !== 'string' || Number.isNaN(Date.parse(updatedAt)))) ||
    ((version === null || updatedAt === null) &&
      !(version === null && updatedAt === null && code === null))
  ) {
    return null;
  }

  return {
    companyId,
    code: code as string | null,
    version: version as number | null,
    updatedAt: updatedAt as string | null
  };
};

const parsePlatformCompanyInventoryItem = (
  value: unknown
): PlatformCompanyInventoryItem | null => {
  if (!isRecord(value) || !isRecord(value.company)) return null;

  const companyId = positiveInteger(value.company.id);
  const logo = optionalString(value.company.logo);
  const primaryColor = optionalString(value.company.primaryColor);
  const secondaryColor = optionalString(value.company.secondaryColor);
  const mobileUserCount = nonNegativeInteger(value.company.mobileUserCount);
  const clientCount = nonNegativeInteger(value.company.clientCount ?? 0);
  if (
    companyId === null ||
    typeof value.company.name !== 'string' ||
    value.company.name.trim().length === 0 ||
    logo === undefined ||
    primaryColor === undefined ||
    secondaryColor === undefined ||
    typeof value.company.lifecycleStatus !== 'string' ||
    value.company.lifecycleStatus.trim().length === 0 ||
    mobileUserCount === null ||
    clientCount === null ||
    typeof value.company.createdAt !== 'string' ||
    Number.isNaN(Date.parse(value.company.createdAt)) ||
    value.tenantEntryPath !== `/platform/companies/${companyId}`
  ) {
    return null;
  }

  return {
    company: {
      id: companyId,
      name: value.company.name,
      logo,
      primaryColor,
      secondaryColor,
      lifecycleStatus: value.company.lifecycleStatus,
      mobileUserCount,
      clientCount,
      createdAt: value.company.createdAt
    },
    tenantEntryPath: value.tenantEntryPath
  };
};

export const parsePlatformCompanyInventory = (
  value: unknown
): PlatformCompanyInventoryData | null => {
  if (
    !isRecord(value) ||
    value.role !== 'SUPER_ADMIN' ||
    !Array.isArray(value.items) ||
    value.items.length > 100
  ) {
    return null;
  }

  const actorId = positiveInteger(value.actorId);
  const totalCount = nonNegativeInteger(value.totalCount);
  const nextAfterCompanyId =
    value.nextAfterCompanyId === null
      ? null
      : positiveInteger(value.nextAfterCompanyId);
  if (
    actorId === null ||
    totalCount === null ||
    (nextAfterCompanyId === null && value.nextAfterCompanyId !== null)
  ) {
    return null;
  }

  const items: PlatformCompanyInventoryItem[] = [];
  let previousCompanyId = 0;
  for (const candidate of value.items) {
    const item = parsePlatformCompanyInventoryItem(candidate);
    if (!item || item.company.id <= previousCompanyId) return null;
    items.push(item);
    previousCompanyId = item.company.id;
  }

  if (
    nextAfterCompanyId !== null &&
    (items.length === 0 ||
      nextAfterCompanyId !== items[items.length - 1].company.id)
  ) {
    return null;
  }

  return {
    actorId,
    role: 'SUPER_ADMIN',
    items,
    totalCount,
    nextAfterCompanyId
  };
};

const boundedNonEmptyString = (
  value: unknown,
  maximum: number
): string | null =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.length <= maximum
    ? value
    : null;

const parsePlatformCompanyChangeHistoryItem = (
  value: unknown
): PlatformCompanyChangeHistoryItem | null => {
  if (!isRecord(value) || !isRecord(value.actor)) return null;

  const id = positiveInteger(value.id);
  const actorId = positiveInteger(value.actor.id);
  const action = boundedNonEmptyString(value.action, 120);
  const resourceType = boundedNonEmptyString(value.resourceType, 120);
  const resourceId = optionalString(value.resourceId);
  const summary = boundedNonEmptyString(value.summary, 500);
  const createdAt =
    typeof value.createdAt === 'string' &&
    !Number.isNaN(Date.parse(value.createdAt))
      ? value.createdAt
      : null;
  if (
    id === null ||
    actorId === null ||
    value.actor.role !== 'SUPER_ADMIN' ||
    action === null ||
    resourceType === null ||
    resourceId === undefined ||
    (resourceId !== null && resourceId.length > 240) ||
    summary === null ||
    createdAt === null
  ) {
    return null;
  }

  return {
    id,
    actor: { id: actorId, role: 'SUPER_ADMIN' },
    action,
    resourceType,
    resourceId,
    summary,
    createdAt
  };
};

export const parsePlatformCompanyChangeHistory = (
  value: unknown,
  expectedCompanyId: number
): PlatformCompanyChangeHistoryData | null => {
  if (
    !isRecord(value) ||
    !Number.isSafeInteger(expectedCompanyId) ||
    expectedCompanyId <= 0 ||
    value.role !== 'SUPER_ADMIN' ||
    !Array.isArray(value.items) ||
    value.items.length > 100
  ) {
    return null;
  }

  const actorId = positiveInteger(value.actorId);
  const companyId = positiveInteger(value.companyId);
  const nextBeforeAuditId =
    value.nextBeforeAuditId === null
      ? null
      : positiveInteger(value.nextBeforeAuditId);
  if (
    actorId === null ||
    companyId !== expectedCompanyId ||
    (nextBeforeAuditId === null && value.nextBeforeAuditId !== null)
  ) {
    return null;
  }

  const items: PlatformCompanyChangeHistoryItem[] = [];
  let previousAuditId = Number.POSITIVE_INFINITY;
  for (const candidate of value.items) {
    const item = parsePlatformCompanyChangeHistoryItem(candidate);
    if (!item || item.id >= previousAuditId) return null;
    items.push(item);
    previousAuditId = item.id;
  }
  if (
    nextBeforeAuditId !== null &&
    (items.length === 0 || nextBeforeAuditId !== items[items.length - 1].id)
  ) {
    return null;
  }

  return {
    actorId,
    role: 'SUPER_ADMIN',
    companyId,
    items,
    nextBeforeAuditId
  };
};

const parseCompanyPendingApplication = (
  value: unknown,
  expectedCompanyId: number,
  expectedStatus: CompanyApplicationStatus
): CompanyApplication | null => {
  if (!isRecord(value) || !isRecord(value.worker)) return null;

  const id = positiveInteger(value.id);
  const companyId = positiveInteger(value.companyId);
  const workerId = positiveInteger(value.worker.id);
  const appliedAt =
    value.appliedAt === null
      ? null
      : typeof value.appliedAt === 'string' &&
        !Number.isNaN(Date.parse(value.appliedAt))
      ? value.appliedAt
      : undefined;
  if (
    id === null ||
    companyId !== expectedCompanyId ||
    workerId === null ||
    value.internalStatus !== expectedStatus ||
    appliedAt === undefined ||
    typeof value.worker.firstName !== 'string' ||
    typeof value.worker.lastName !== 'string' ||
    typeof value.worker.email !== 'string' ||
    value.worker.email.trim().length === 0 ||
    typeof value.worker.profileImage !== 'string'
  ) {
    return null;
  }

  return {
    id,
    companyId,
    internalStatus: expectedStatus,
    appliedAt,
    worker: {
      id: workerId,
      firstName: value.worker.firstName,
      lastName: value.worker.lastName,
      email: value.worker.email,
      profileImage: value.worker.profileImage
    }
  };
};

export const parseCompanyApplications = (
  value: unknown,
  expectedCompanyId: number,
  expectedStatus: CompanyApplicationStatus
): CompanyApplication[] | null => {
  if (
    !Number.isSafeInteger(expectedCompanyId) ||
    expectedCompanyId <= 0 ||
    !Array.isArray(value) ||
    value.length > 1000
  ) {
    return null;
  }

  const applications: CompanyApplication[] = [];
  const seenIds = new Set<number>();
  for (const candidate of value) {
    const application = parseCompanyPendingApplication(
      candidate,
      expectedCompanyId,
      expectedStatus
    );
    if (!application || seenIds.has(application.id)) return null;
    applications.push(application);
    seenIds.add(application.id);
  }
  return applications;
};

export const parseCompanyPendingApplications = (
  value: unknown,
  expectedCompanyId: number
): CompanyApplication[] | null =>
  parseCompanyApplications(value, expectedCompanyId, 'pending');

export const parseCompanyMembershipDecision = (
  value: unknown,
  expectedCompanyId: number,
  expectedMembershipId: number
): CompanyMembershipDecisionData | null => {
  if (!isRecord(value)) return null;
  const membershipId = positiveInteger(value.membershipId);
  const companyId = positiveInteger(value.companyId);
  const workerAccountId = positiveInteger(value.workerAccountId);
  const fromStatus = value.fromStatus;
  const toStatus = value.toStatus;
  const workerStatus = value.workerStatus;
  const notificationDispatch = value.notificationDispatch;
  if (
    membershipId !== expectedMembershipId ||
    companyId !== expectedCompanyId ||
    workerAccountId === null ||
    (fromStatus !== 'pending' && fromStatus !== 'rejected') ||
    (toStatus !== 'approved' &&
      toStatus !== 'rejected' &&
      toStatus !== 'cancelled') ||
    (workerStatus !== 'pending' &&
      workerStatus !== 'approved' &&
      workerStatus !== 'cancelled') ||
    typeof value.canReapply !== 'boolean' ||
    (notificationDispatch !== 'not_required' &&
      notificationDispatch !== 'queued' &&
      notificationDispatch !== 'attention_required')
  ) {
    return null;
  }
  return {
    membershipId,
    companyId,
    workerAccountId,
    fromStatus,
    toStatus,
    workerStatus,
    canReapply: value.canReapply,
    notificationDispatch
  };
};
